import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaImages, FaUpload, FaDownload } from 'react-icons/fa';
import { getMyPhotos, getImageUrl, downloadPhoto } from '../services/api';
import { useAuth } from '../context/AuthContext';

function MyPhotos() {
  const { user } = useAuth();
  const [photoGroups, setPhotoGroups] = useState([]);
  const [totals, setTotals] = useState({ photos: 0, events: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await getMyPhotos();
      setPhotoGroups(response.data);
      setTotals({ photos: response.total_photos, events: response.total_events });
    } catch (err) {
      setError(err.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Loading your photos...</p>
      </div>
    );
  }

  if (!user?.profile_photo) {
    return (
      <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <FaUpload style={{ fontSize: '4rem', color: 'var(--primary-orange)', marginBottom: '1rem' }} />
        <h2 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>
          No Profile Photo Yet
        </h2>
        <p style={{ color: 'var(--gray)', marginBottom: '2rem', fontSize: '1.1rem' }}>
          Upload your profile photo to start discovering photos where you appear!
        </p>
        <Link to="/profile-upload" className="btn-primary">
          Upload Profile Photo
        </Link>
      </div>
    );
  }

  if (photoGroups.length === 0) {
    return (
      <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <FaImages style={{ fontSize: '4rem', color: 'var(--gray)', marginBottom: '1rem' }} />
        <h2 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>
          No Matches Found Yet
        </h2>
        <p style={{ color: 'var(--gray)', fontSize: '1.1rem' }}>
          We haven't found any photos matching your profile yet.
          Check back later as photographers upload more event photos!
        </p>
      </div>
    );
  }

  return (
    <div className="photo-gallery">
      <div className="container">
        <h1 style={{ color: 'var(--primary-orange)', marginBottom: '1rem' }}>
          Your Discovered Photos
        </h1>
        <p style={{ color: 'var(--gray)', marginBottom: '2rem', fontSize: '1.1rem' }}>
          Found {totals.photos} photos across {totals.events} events
        </p>

        {photoGroups.map((group) => (
          <div key={group.event_id} className="event-section">
            <div className="event-header">
              <h3>{group.event_name}</h3>
              <div className="event-meta">
                <span className="category-badge">{group.event_category}</span>
                {group.event_date && (
                  <span>📅 {new Date(group.event_date).toLocaleDateString()}</span>
                )}
                <span>📸 {group.photos.length} photos</span>
              </div>
            </div>

            <div className="photos-grid">
              {group.photos.map((photo) => (
                <div key={photo.id} className="photo-card">
                  <img
                    src={getImageUrl(photo.filename)}
                    alt={`Match from ${group.event_name}`}
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/300x200/png';
                    }}
                  />
                  <div className="photo-info">
                    <div className="confidence-score">
                      {photo.confidence_score}% Match
                    </div>
                    <div className="confidence-bar">
                      <div
                        className="confidence-fill"
                        style={{ width: `${photo.confidence_score}%` }}
                      ></div>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
                      Uploaded: {new Date(photo.uploaded_at).toLocaleDateString()}
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          const blob = await downloadPhoto(photo.id);
                          const url = window.URL.createObjectURL(new Blob([blob]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = photo.filename.split('/').pop();
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error('Download failed:', err);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        marginTop: '0.5rem',
                        background: 'var(--primary-green)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      <FaDownload /> Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MyPhotos;
